/**
 * Aranabilir açılır seçim kutusu (Popover + cmdk Command).
 * Yazılan karakterlere göre liste filtrelenir; her öğe `keywords` alanındaki
 * metinlere göre eşleşir (ör. müşteri adı, telefon, plaka).
 *
 * items: [{ value, label, keywords }]
 *   value    -> seçim kimliği (id)
 *   label    -> görünen metin (string veya JSX)
 *   keywords -> arama için ek metinler (string[])
 */
import { useState } from "react";
import { Check, ChevronsUpDown } from "lucide-react";
import { Button } from "./ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "./ui/popover";
import {
  Command,
  CommandInput,
  CommandList,
  CommandEmpty,
  CommandGroup,
  CommandItem,
} from "./ui/command";
import { cn } from "../lib/utils";

export default function Combobox({
  value,
  onChange,
  items = [],
  placeholder = "Seçin",
  searchPlaceholder = "Ara...",
  emptyText = "Sonuç bulunamadı",
  disabled = false,
  testId,
  className,
}) {
  const [open, setOpen] = useState(false);
  const selected = items.find((i) => i.value === value);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="outline"
          role="combobox"
          aria-expanded={open}
          disabled={disabled}
          className={cn(
            "w-full justify-between rounded-sm font-normal h-10",
            // Seçim yapılmamışken gösterilen metin bir PLACEHOLDER'dır; Input/
            // Select placeholder tonuyla aynı olmalı (bkz. ui/input.jsx).
            !selected && "text-muted-foreground/45",
            className
          )}
          data-testid={testId}
        >
          <span className="truncate">{selected ? selected.label : placeholder}</span>
          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent
        className="p-0 w-[var(--radix-popover-trigger-width)] min-w-[240px]"
        align="start"
      >
        <Command>
          <CommandInput placeholder={searchPlaceholder} />
          <CommandList>
            <CommandEmpty>{emptyText}</CommandEmpty>
            <CommandGroup>
              {items.map((item) => (
                <CommandItem
                  key={item.value}
                  value={item.value}
                  keywords={item.keywords}
                  onSelect={() => {
                    onChange(item.value);
                    setOpen(false);
                  }}
                >
                  <Check
                    className={cn(
                      "mr-2 h-4 w-4 shrink-0",
                      value === item.value ? "opacity-100" : "opacity-0"
                    )}
                  />
                  <span className="truncate">{item.label}</span>
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
